import {
  useEffect,
  useRef,
  useState,
  type AnchorHTMLAttributes,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  ArrowUp,
  ChevronDown,
  ExternalLink,
  Maximize2,
  MessageCircle,
  Minimize2,
} from "lucide-react";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { ChatMessageRole } from "@KassaroAB/shared/types/enums/ChatMessageRole";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import {
  HelpSourceScheme,
  type HelpChatMessage,
  type HelpSource,
} from "@KassaroAB/shared/types/help";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../config/firebase";
import { streamHelpChatApi } from "../../api/helpApi";
import { NAVBAR_HEIGHT } from "../../components/Navbar";
import { ApiError } from "../../utils/api";
import { helpSiteUrl } from "../../utils/helpSite";
import { useHelpCenterHref } from "./useHelpCenterHref";
import { useHelpStoreId } from "./useHelpStoreId";

const MAX_STORED_MESSAGES = 40;

const StoredMessageScheme = z.object({
  role: z.enum(ChatMessageRole),
  content: z.string(),
  sources: z.array(HelpSourceScheme),
  failed: z.boolean().optional(),
});

const StoredChatScheme = z.object({
  open: z.boolean(),
  expanded: z.boolean().optional(),
  messages: z.array(StoredMessageScheme),
});

type StoredMessage = z.infer<typeof StoredMessageScheme>;
type StoredChat = z.infer<typeof StoredChatScheme>;

const emptyChat: StoredChat = { open: false, messages: [] };

const storageKey = (uid: string) => `kassaro-admin-help-chat:${uid}`;

const loadChat = (uid: string): Promise<StoredChat> =>
  Promise.resolve()
    .then(() => localStorage.getItem(storageKey(uid)))
    .then((raw) => (raw ? StoredChatScheme.parse(JSON.parse(raw)) : emptyChat))
    .catch((error: unknown) => {
      console.warn("Discarding stored help chat", error);
      return Promise.resolve()
        .then(() => localStorage.removeItem(storageKey(uid)))
        .catch((removeError: unknown) =>
          console.warn("Could not clear help chat", removeError),
        )
        .then(() => emptyChat);
    });

const saveChat = (uid: string, chat: StoredChat): Promise<void> =>
  Promise.resolve()
    .then(() =>
      localStorage.setItem(
        storageKey(uid),
        JSON.stringify({
          ...chat,
          messages: chat.messages.slice(-MAX_STORED_MESSAGES),
        }),
      ),
    )
    .catch((error: unknown) => console.warn("Could not save help chat", error));

const uniqueSources = (sources: HelpSource[]): HelpSource[] =>
  sources.filter(
    (source, index) =>
      sources.findIndex((item) => item.path === source.path) === index,
  );

const linkedPaths = (markdown: string): Set<string> =>
  new Set(
    Array.from(markdown.matchAll(/\]\((\/[^)\s#?]*)/g), ([, path = ""]) =>
      path.replace(/(.)\/$/, "$1"),
    ),
  );

const Root = styled.div`
  position: fixed;
  right: ${spacing.x3}px;
  bottom: ${spacing.x3}px;
  z-index: 900;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${spacing.x2}px;

  @media (max-width: 768px) {
    right: ${spacing.x2}px;
    bottom: calc(${spacing.x2}px + env(safe-area-inset-bottom, 0px));
  }
`;

const Launcher = styled.button<{ $open: boolean; $expanded: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.x1}px;
  height: 52px;
  padding: 0 ${({ $open }) => ($open ? 14 : 20)}px;
  border: none;
  border-radius: 26px;
  background: ${colors.primary};
  color: ${colors.primaryContrastText};
  font-size: 16px;
  font-weight: 600;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.3);
  cursor: pointer;
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 36px rgba(15, 23, 42, 0.35);
  }

  ${({ $open, $expanded }) =>
    $open &&
    $expanded &&
    css`
      @media (min-width: 769px) {
        display: none;
      }
    `}
`;

const Window = styled.section<{ $expanded: boolean }>`
  display: flex;
  flex-direction: column;
  width: min(400px, calc(100vw - 32px));
  height: min(600px, calc(100dvh - ${NAVBAR_HEIGHT + 120}px));
  overflow: hidden;
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.large}px;
  background: ${colors.backgroundLight};
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.25);

  ${({ $expanded }) =>
    $expanded &&
    css`
      @media (min-width: 769px) {
        width: min(1000px, calc(100vw - ${spacing.x3 * 2}px));
        height: calc(100dvh - ${NAVBAR_HEIGHT + spacing.x3 * 2}px);
      }
    `}
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
  padding: ${spacing.x1}px ${spacing.x2}px;
  background: ${colors.primary};
  color: ${colors.primaryContrastText};

  strong {
    flex: 1;
  }
`;

const HeaderButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: ${borderRadius.regular}px;
  background: transparent;
  color: inherit;
  font-size: 13px;
  cursor: pointer;
  text-decoration: none;
`;

const ExpandButton = styled(HeaderButton)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const Messages = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
  padding: ${spacing.x2}px;
  overflow-y: auto;
`;

const Intro = styled.p`
  margin: 0;
  padding: ${spacing.x1}px ${spacing.x2}px;
  border-radius: ${borderRadius.regular}px;
  background: ${colors.background};
  color: ${colors.textMuted};
  font-size: 15px;
`;

const Bubble = styled.div<{ $user: boolean; $failed?: boolean }>`
  align-self: ${({ $user }) => ($user ? "flex-end" : "flex-start")};
  max-width: 88%;
  min-width: 0;
  padding: ${spacing.x1}px 13px;
  border-radius: 14px;
  background: ${({ $user }) => ($user ? colors.primary : colors.background)};
  color: ${({ $user, $failed }) =>
    $failed ? colors.danger : $user ? colors.primaryContrastText : colors.text};
  font-size: 15px;
  line-height: 1.55;
  white-space: ${({ $user }) => ($user ? "pre-wrap" : "normal")};
  overflow-wrap: anywhere;

  & > :first-child {
    margin-top: 0;
  }

  & > :last-child {
    margin-bottom: 0;
  }

  p,
  ul,
  ol {
    margin: 0.4em 0;
  }

  ul,
  ol {
    padding-left: 18px;
  }

  a {
    color: ${({ $user }) => ($user ? "inherit" : colors.primary)};
  }

  img {
    max-width: 100%;
  }

  pre {
    max-width: 100%;
    overflow-x: auto;
    white-space: pre;
  }

  table {
    display: block;
    max-width: 100%;
    overflow-x: auto;
    border-collapse: collapse;
  }

  th,
  td {
    padding: 4px 8px;
    overflow-wrap: normal;
    border: 1px solid ${colors.border};
    text-align: left;
  }
`;

const Sources = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
  max-width: 88%;
`;

const SourcePill = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  padding: 4px 10px;
  border: 1px solid ${colors.border};
  border-radius: 999px;
  background: ${colors.backgroundLight};
  color: ${colors.primary};
  font-size: 13px;
  text-decoration: none;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  svg {
    flex-shrink: 0;
  }

  &:hover {
    background: ${colors.background};
  }
`;

const Composer = styled.form`
  display: flex;
  align-items: flex-end;
  gap: ${spacing.x1}px;
  padding: ${spacing.x1}px 12px;
  border-top: 1px solid ${colors.border};
`;

const Input = styled.textarea`
  flex: 1;
  max-height: 140px;
  resize: none;
  padding: 9px 12px;
  border: 1px solid ${colors.border};
  border-radius: 12px;
  font: inherit;
  font-size: 15px;
`;

const SendButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: ${colors.primary};
  color: ${colors.primaryContrastText};
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: progress;
  }
`;

const helpLinkHref = (href: string | undefined, storeId: StoreId | null) =>
  href?.startsWith("/") ? helpSiteUrl(href, storeId) : href;

const AssistantMarkdown = ({
  markdown,
  storeId,
}: {
  markdown: string;
  storeId: StoreId | null;
}) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    components={{
      a: ({ href, children }: AnchorHTMLAttributes<HTMLAnchorElement>) => (
        <a
          href={helpLinkHref(href, storeId)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {children}
        </a>
      ),
    }}
  >
    {markdown}
  </ReactMarkdown>
);

export const HelpChatBubble = () => {
  const { t } = useTranslation();
  const storeId = useHelpStoreId();
  const helpCenterHref = useHelpCenterHref();
  const [chat, setChat] = useState<StoredChat>(emptyChat);
  const [uid, setUid] = useState<string | null>(auth.currentUser?.uid ?? null);
  const [loadedUid, setLoadedUid] = useState<string | null>(null);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const uidRef = useRef<string | null>(uid);
  const conversationRef = useRef(0);
  const requestRef = useRef<AbortController | null>(null);

  useEffect(
    () => onAuthStateChanged(auth, (user) => setUid(user?.uid ?? null)),
    [],
  );

  useEffect(() => {
    uidRef.current = uid;
    conversationRef.current += 1;
    requestRef.current?.abort();
    requestRef.current = null;
    setLoadedUid(null);
    setChat(emptyChat);
    if (!uid) {
      return;
    }
    let cancelled = false;
    loadChat(uid).then((stored) => {
      if (cancelled) {
        return;
      }
      setChat(stored);
      setLoadedUid(uid);
    });
    return () => {
      cancelled = true;
    };
  }, [uid]);

  useEffect(() => {
    if (uid && loadedUid === uid) {
      void saveChat(uid, chat);
    }
  }, [chat, uid, loadedUid]);

  useEffect(() => {
    messagesRef.current?.scrollTo({ top: messagesRef.current.scrollHeight });
  }, [chat.messages, chat.open]);

  useEffect(() => {
    if (chat.open) {
      inputRef.current?.focus();
    }
  }, [chat.open]);

  const updateLast = (update: (message: StoredMessage) => StoredMessage) =>
    setChat((current) => ({
      ...current,
      messages: [
        ...current.messages.slice(0, -1),
        ...current.messages.slice(-1).map(update),
      ],
    }));

  const ask = async (history: HelpChatMessage[]) => {
    const requestUid = uidRef.current;
    const requestConversation = conversationRef.current;
    const isCurrent = () =>
      uidRef.current === requestUid &&
      conversationRef.current === requestConversation;
    const controller = new AbortController();
    requestRef.current = controller;
    for await (const event of streamHelpChatApi(
      history,
      storeId,
      controller.signal,
    )) {
      if (!isCurrent()) {
        return;
      }
      if (event.type === "delta") {
        updateLast((message) => ({
          ...message,
          content: message.content + event.text,
        }));
      }
      if (event.type === "sources") {
        updateLast((message) => ({
          ...message,
          sources: uniqueSources([...message.sources, ...event.sources]),
        }));
      }
      if (event.type === "error") {
        throw new Error(event.message);
      }
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const content = question.trim();
    if (!content || busy) {
      return;
    }
    const history: HelpChatMessage[] = [
      ...chat.messages
        .filter((message) => !message.failed)
        .map(({ role, content: text }) => ({ role, content: text })),
      { role: ChatMessageRole.USER, content },
    ].slice(-20);
    setQuestion("");
    setBusy(true);
    setChat((current) => ({
      ...current,
      messages: [
        ...current.messages,
        { role: ChatMessageRole.USER, content, sources: [] },
        { role: ChatMessageRole.ASSISTANT, content: "", sources: [] },
      ],
    }));
    const requestUid = uidRef.current;
    const requestConversation = conversationRef.current;
    ask(history)
      .catch((error: unknown) => {
        if (
          uidRef.current !== requestUid ||
          conversationRef.current !== requestConversation
        ) {
          return;
        }
        const message =
          error instanceof ApiError && error.statusCode === 429
            ? t("help.rateLimited")
            : t("help.error");
        updateLast((last) => ({ ...last, content: message, failed: true }));
      })
      .finally(() => setBusy(false));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      formRef.current?.requestSubmit();
    }
  };

  const setOpen = (open: boolean) =>
    setChat((current) => ({ ...current, open }));

  const expanded = chat.expanded ?? false;

  return createPortal(
    <Root>
      {chat.open && (
        <Window aria-label={t("help.title")} $expanded={expanded}>
          <Header>
            <strong>{t("help.title")}</strong>
            <HeaderButton
              as="a"
              href={helpCenterHref}
              target="_blank"
              rel="noopener"
            >
              {t("help.center")}
              <ExternalLink size={12} />
            </HeaderButton>
            {chat.messages.length > 0 && (
              <HeaderButton
                type="button"
                onClick={() => {
                  conversationRef.current += 1;
                  requestRef.current?.abort();
                  requestRef.current = null;
                  setBusy(false);
                  setChat((current) => ({ ...current, messages: [] }));
                }}
              >
                {t("help.newConversation")}
              </HeaderButton>
            )}
            <ExpandButton
              type="button"
              aria-label={t(expanded ? "help.shrink" : "help.expand")}
              title={t(expanded ? "help.shrink" : "help.expand")}
              onClick={() =>
                setChat((current) => ({ ...current, expanded: !expanded }))
              }
            >
              {expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </ExpandButton>
            <HeaderButton
              type="button"
              aria-label={t("help.minimize")}
              onClick={() => setOpen(false)}
            >
              <ChevronDown size={16} />
            </HeaderButton>
          </Header>
          <Messages ref={messagesRef} aria-live="polite">
            {chat.messages.length === 0 && <Intro>{t("help.askIntro")}</Intro>}
            {chat.messages.map((message, index) => {
              if (message.role === ChatMessageRole.USER) {
                return (
                  <Bubble key={index} $user>
                    {message.content}
                  </Bubble>
                );
              }
              const linked = linkedPaths(message.content);
              const sources = message.failed
                ? []
                : message.sources
                    .filter((source) => !linked.has(source.path))
                    .slice(0, 5);
              const body = message.failed ? (
                message.content
              ) : (
                <AssistantMarkdown
                  markdown={message.content}
                  storeId={storeId}
                />
              );
              return (
                <div key={index}>
                  <Bubble $user={false} $failed={message.failed}>
                    {message.content ? body : t("help.thinking")}
                  </Bubble>
                  {sources.length > 0 && (
                    <Sources>
                      {sources.map((source) => (
                        <SourcePill
                          key={source.path}
                          href={helpSiteUrl(
                            source.anchor
                              ? `${source.path}#${source.anchor}`
                              : source.path,
                            storeId,
                          )}
                          target="_blank"
                          rel="noopener"
                        >
                          <span>{source.title}</span>
                          <ExternalLink size={12} />
                        </SourcePill>
                      ))}
                    </Sources>
                  )}
                </div>
              );
            })}
          </Messages>
          <Composer ref={formRef} onSubmit={handleSubmit}>
            <Input
              ref={inputRef}
              rows={1}
              value={question}
              maxLength={4000}
              placeholder={t("help.askPlaceholder")}
              onChange={(event) => setQuestion(event.target.value)}
              onKeyDown={handleKeyDown}
            />
            <SendButton
              type="submit"
              disabled={busy}
              aria-label={t("help.send")}
            >
              <ArrowUp size={18} />
            </SendButton>
          </Composer>
        </Window>
      )}
      <Launcher
        type="button"
        $open={chat.open}
        $expanded={expanded}
        aria-expanded={chat.open}
        onClick={() => setOpen(!chat.open)}
      >
        {chat.open ? (
          <ChevronDown size={22} />
        ) : (
          <>
            <MessageCircle size={22} />
            {t("help.title")}
          </>
        )}
      </Launcher>
    </Root>,
    document.body,
  );
};
